import move from 'lodash-move';
import Link from 'next/link';
import {Suspense, useState} from 'react';
import {publishEvent} from '@udemy-v2/event-tracking';

import {FlipCard} from 'src/components/flip-card/flip-card';
import {Loading} from 'src/components/loading/loading';
import {CourseCTAClicked, UBCTAClicked} from './events';
import {FilledButton} from '@/components/button/filled-button';
import {OutlineButton} from '@/components/button/outline-button';
import assetLoader from '@/utils/asset-loader';
import VideoPlayer from './video-player';
import {CardStack} from '@/components/card-stack/card-stack';

import styles from './featured-courses.module.scss';

const handleBookDemo = () => {
    publishEvent(new UBCTAClicked());
};

const handleStartLearning = () => {
    publishEvent(new CourseCTAClicked());
};

const deckCoursesData = [
    {
        url: assetLoader({
            src: '/ci/images/landing/placeholders/courses/1.jpg',
        }),
        title: 'Make an AI personal assistant',
        link: 'https://www.udemy.com/course/how-ai-is-changing-business/',
        number: '01',
        description:
            'Find out how to create your own custom AI assistant to scale knowledge and capabilities.',
    },
    {
        url: assetLoader({
            src: '/ci/images/landing/placeholders/courses/2.jpg',
        }),
        title: 'Add AI to your workflows',
        link: 'https://www.udemy.com/course/how-to-lead-teams-through-ai-driven-change/',
        number: '02',
        description:
            'Learn to make a habit of tackling tasks with AI across teams and functions, including operations, marketing, finance, HR, and more.',
    },
    {
        url: assetLoader({
            src: '/ci/images/landing/placeholders/courses/3.jpg',
        }),
        title: 'Create a culture of experimentation',
        link: 'https://www.udemy.com/course/how-to-identify-practical-uses-of-ai-in-your-role/',
        number: '03',
        description:
            'Discover how to get buy-in on AI tools by having your teams try out ChatGPT, Claude, and more for quick iteration and innovation.',
    },
];

function getFlipCards(
    setIsFlipped: (isFlipped: boolean) => void,
): {id: string; children: React.ReactNode}[] {
    return deckCoursesData.map((item, index) => ({
        id: index.toString(),
        children: (
            <FlipCard
                key={index}
                number={`0${index + 1}`}
                frontTitle={item.title}
                backTitle={item.title}
                backgroundImage={item.url}
                onFlip={(flipped) => setIsFlipped(flipped)}
                description={
                    <div className={styles.cardDescription}>
                        <p>{item.description}</p>
                    </div>
                }
            />
        ),
    }));
}

const FeaturedCourses = () => {
    const [autoPlay, setAutoPlay] = useState(true);
    const [isFlipped, setIsFlipped] = useState(false);
    const [deckData, setDeckData] = useState(getFlipCards((value) => setIsFlipped(value)));

    return (
        <div className={styles.container} id="new-course">
            <span className={styles.title}>New course: AI for Business Leaders</span>
            <span className={styles.subtitle}>
                Learn AI fundamentals and how to kickstart AI adoption across your organization.
            </span>
            <div className={styles.videoContainer}>
                <VideoPlayer />
            </div>
            <div className={styles.courseInfo}>
                <FlipCard
                    number={null}
                    frontTitle={'About your instructor'}
                    backTitle={'About your instructor'}
                    backgroundImage={assetLoader({
                        src: '/ci/images/landing/placeholders/cards/webp/4.webp',
                    })}
                    description={
                        <div className={styles.cardDescription}>
                            <p>
                                Helen Lee Kupp is the co-founder and creator of Women Defining AI.
                            </p>
                            <p>
                                She offers a unique perspective on how we’ll operate in the future
                                by connecting humans working flexibly with AI. In this course, she
                                shares how to drive AI adoption through practical experimentation.
                            </p>
                        </div>
                    }
                />
                <div className={styles.carouselContainer}>
                    <Suspense fallback={<Loading />}>
                        <CardStack
                            isFlipped={isFlipped}
                            autoPlay={autoPlay}
                            setAutoPlay={setAutoPlay}
                            data={deckData}
                            onMoveToEnd={(from) =>
                                setDeckData(move(deckData, from, deckData.length - 1))
                            }
                        />
                    </Suspense>
                </div>
            </div>
            <span className={styles.ctaTitle}>
                Take the course now, or explore plans for your team
            </span>
            <div className={styles.ctaButtons}>
                <Link
                    href="https://www.udemy.com/course/ai-for-business-leaders-with-helen-lee-kupp"
                    target="_blank"
                >
                    <FilledButton onClick={handleStartLearning}>Start learning</FilledButton>
                </Link>

                <Link
                    href="https://business.udemy.com/request-demo/?ref=InnovationStudio"
                    target="_blank"
                >
                    <OutlineButton onClick={handleBookDemo} className="right">
                        Book a demo
                    </OutlineButton>
                </Link>
            </div>
        </div>
    );
};

export {FeaturedCourses};
